import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Calendar, Users, Scissors, Settings, LogOut, Link, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import API from "@/lib/api";

const navItems = [
  { to: "/tableau-de-bord", icon: LayoutDashboard, label: "Accueil", end: true },
  { to: "/tableau-de-bord/calendrier", icon: Calendar, label: "Calendrier" },
  { to: "/tableau-de-bord/clients", icon: Users, label: "Clients" },
  { to: "/tableau-de-bord/services", icon: Scissors, label: "Services" },
  { to: "/tableau-de-bord/parametres", icon: Settings, label: "Param." },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    API.get("/barbershop").then(r => setShop(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const bookingUrl = shop?.slug ? `${window.location.origin}/book/${shop.slug}` : null;

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-[240px] flex-col fixed inset-y-0 left-0 bg-white border-r border-border z-30">
        <div className="p-6 border-b border-border">
          <h1 className="font-heading font-bold text-xl tracking-tight">CutIt</h1>
          {shop && <p className="text-xs text-muted-foreground mt-1 truncate">{shop.name}</p>}
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "active bg-accent font-semibold" : "text-muted-foreground hover:bg-accent/50"}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace('.', '')}`}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        {bookingUrl && (
          <div className="p-4 border-t border-border">
            <button
              onClick={() => { navigator.clipboard.writeText(bookingUrl); }}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
              data-testid="copy-booking-link"
            >
              <Link size={14} />
              <span className="truncate">Copier le lien de reservation</span>
            </button>
          </div>
        )}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="shrink-0">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-border flex items-center justify-between px-4 z-30">
        <h1 className="font-heading font-bold text-lg">CutIt</h1>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMobileOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-64 bg-white p-4 pt-16 space-y-1 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-accent font-semibold" : "text-muted-foreground hover:bg-accent/50"}`
                }
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            ))}
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-destructive hover:bg-destructive/10 w-full mt-4">
              <LogOut size={18} />
              Deconnexion
            </button>
          </div>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 h-16 bg-white border-t border-border flex items-center justify-around z-30">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 text-[10px] py-1 px-2 ${isActive ? "text-primary font-semibold" : "text-muted-foreground"}`
            }
          >
            <item.icon size={20} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Main Content */}
      <main className="flex-1 lg:ml-[240px] pt-14 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
